import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { Avatar, ButtonBase, FormControl, TextField, FormLabel, Typography } from "@mui/material";

import GroupFallbackImage from "../../assets/images/group-fallback-avatar.png";
import { UploadGroupAvatarIcon } from "../../assets/icons/groups-icons";
import { GroupFormData } from "../../types/group.types";
import { useSingleMediaUpload } from "../../../../api/v1/mutations/useMediaUpload";
import { formatImgUrl } from "../../../../utils/cardFormating";

export const GroupInfo = () => {
  const { t } = useTranslation("groups");
  const { control, setValue } = useFormContext<GroupFormData>();
  const { mutate: uploadMedia } = useSingleMediaUpload();

  const [previewImage, setPreviewImage] = useState<string | null>(null);

  useEffect(() => {
    const currentPreview = previewImage;
    return () => {
      if (currentPreview) {
        URL.revokeObjectURL(currentPreview);
      }
    };
  }, [previewImage]);

  const getAvatarSrc = (value?: string) => {
    if (!value) return undefined;
    if (value.startsWith("http")) return value;
    return formatImgUrl(value);
  };

  const handleAvatarChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setPreviewImage(previewUrl);

      uploadMedia(
        {
          file,
          onProgress: (progress) => {},
        },
        {
          onSuccess: (response) => {
            setValue("imageLink", response.fileName);
          },
          onError: (error) => {
            console.error("Failed to upload image:", error);
            // TODO: Show error message to user
          },
        }
      );
    }
  };

  return (
    <>
      <Typography
        sx={{
          color: "var(--Dark---1000, #111)",
          fontFamily: "Montserrat",
          fontSize: "16px",
          fontStyle: "normal",
          fontWeight: 700,
          lineHeight: "normal",
        }}
      >
        {t("defineTheGroup")}
      </Typography>
      <Controller
        name="imageLink"
        control={control}
        render={({ field }) => (
          <ButtonBase
            component="label"
            role={undefined}
            tabIndex={-1}
            aria-label="Avatar image"
            sx={{
              position: "relative",
              borderRadius: "40px",
              "&:has(:focus-visible)": {
                outline: "2px solid",
                outlineOffset: "2px",
              },
            }}
          >
            <UploadGroupAvatarIcon
              sx={{
                position: "absolute",
                bottom: "-2px",
                right: "-1px",
                zIndex: 1,
              }}
            />
            <Avatar
              alt="Upload new avatar"
              src={previewImage || getAvatarSrc(field.value) || GroupFallbackImage}
              sx={{
                width: "76px",
                height: "76px",
              }}
            />
            <input
              type="file"
              accept="image/*"
              style={{
                border: 0,
                clip: "rect(0 0 0 0)",
                height: "1px",
                margin: "-1px",
                overflow: "hidden",
                padding: 0,
                position: "absolute",
                whiteSpace: "nowrap",
                width: "1px",
              }}
              onChange={handleAvatarChange}
            />
          </ButtonBase>
        )}
      />
      <FormControl sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "4px" }}>
        <FormLabel
          sx={{
            color: "var(--Dark---800, #414141)",
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "24px",
          }}
        >
          {t("groupName")}
        </FormLabel>
        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              error={!!error}
              helperText={error?.message && t(error.message)}
              placeholder={t("enterGroupName")}
            />
          )}
        />
      </FormControl>
      <FormControl sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "4px" }}>
        <FormLabel
          sx={{
            color: "var(--Dark---800, #414141)",
            fontFamily: "Montserrat",
            fontSize: "14px",
            fontStyle: "normal",
            fontWeight: 600,
            lineHeight: "24px",
          }}
        >
          {t("groupDescription")}
        </FormLabel>
        <Controller
          name="description"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              error={!!error}
              helperText={error?.message ? t(error.message) : t("limitedTo200Characters")}
              multiline
              rows={4}
              inputProps={{ maxLength: 200 }}
              placeholder={t("writeSmallDescription")}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": {
                  borderRadius: "8px !important",
                },
              }}
            />
          )}
        />
      </FormControl>
    </>
  );
};
